import React, { useState } from 'react';
import Cookies from 'js-cookie';
import PropTypes from 'prop-types';
import { useApolloClient } from '@apollo/client';
import useContentfulFeature from 'shop/lib/util/client.util/featureFlag';
import Button from '../../../Button/Button';
import { refreshSession } from '../../../Authentication/Authentication.utils';
import { vercel_base_url } from '../../../../util/config';
import { formatPrice } from '../../../../util/localize';

import * as Styled from './CartFooter.styled';
import { useBreakpoint } from '../../../../util/Breakpoint.context';

/**
 * `CartFooter` is a block-level component, used to conditionally display a checkout button, continue shopping button, and discount code.
 */
const CartFooter = ({
  subtotalPrice,
  currencyCode,
  checkoutUrl,
  onCheckoutClick,
  hasGiftableProduct,
  discountCode,
  discountAmount,
  discountPercentage,
  totalItems,
  showViewBagLink,
  className,
  onGiftOptionsClick,
  isGiftingPrioritized,
  siteConfig,
  setViewCouponDialogDisplay,
  onRequestClose
}) => {
  const client = useApolloClient();
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
  const isCartEmpty = totalItems <= 0;
  const siteWideDiscountPercentage = siteConfig?.coupon_code_percentage?.value;
  const isDiscountAutoApplied = siteConfig?.is_discount_auto_applied?.value;
  const enableMiniCartDiscountButton = useContentfulFeature(
    'enableMiniCartDiscountButton'
  );

  const { medium } = useBreakpoint();

  const viewBagText = medium ? 'View Bag' : 'Continue Shopping';

  // On medium screens or mobile cart page, secondary button should be a link
  // In mobile Cart Dialog, secondary button should close the modal
  const secondaryButtonProps =
    medium || (!medium && !onRequestClose)
      ? {
          as: 'a',
          href: medium ? `${vercel_base_url}/cart` : vercel_base_url
        }
      : {
          as: 'button',
          onClick: () => {
            if (onRequestClose) {
              onRequestClose();
            }
          }
        };

  /**
   * Determines whether the mini cart discount button should be displayed.
   *
   * The button is shown if the following conditions are met:
   * - The mini cart discount button feature is enabled.
   * - A discount is not automatically applied.
   * - The 'optimizelyNewCustomerOfferEmailCaptureSuccess' cookie is not set.
   *
   * @type {boolean}
   */
  const showMiniCartDiscountButton =
    enableMiniCartDiscountButton &&
    !isDiscountAutoApplied &&
    !Cookies.get('optimizelyNewCustomerOfferEmailCaptureSuccess');

  if (isCartEmpty && !discountCode) return null;

  return (
    <>
      <Styled.CartFooter className={className}>
        {!isCartEmpty && !isGiftingPrioritized && hasGiftableProduct && (
          <Styled.GiftingButton
            $hasDiscount={!!discountCode}
            data-track-label="add gift options"
            onClick={onGiftOptionsClick}
            aria-label="add gift options"
          >
            <Styled.GiftingText>Make It A Gift?</Styled.GiftingText>
            <Styled.PlusIcon />
          </Styled.GiftingButton>
        )}
        {discountCode && (
          <Styled.DiscountContainer>
            <Styled.CodeWrap>
              <Styled.Prefix>Code Applied:</Styled.Prefix>
              <Styled.Code>
                <Styled.TagIcon />
                <Styled.DiscountCode>
                  {discountCode.toUpperCase()}
                </Styled.DiscountCode>
              </Styled.Code>
            </Styled.CodeWrap>
            <Styled.DiscountBadge>
              {(!!discountAmount &&
                `-${formatPrice(
                  Number(discountAmount).toFixed(2),
                  currencyCode
                ).replace(/\.00$/, '')}`) ||
                `${discountPercentage}% Off`}
            </Styled.DiscountBadge>
          </Styled.DiscountContainer>
        )}
        {!isCartEmpty && !!Number(subtotalPrice) && checkoutUrl && (
          <Styled.ButtonGroup>
            <Button
              as="a"
              variant="primary"
              size="large"
              isFull
              isLoading={isCheckoutLoading}
              href={checkoutUrl}
              onClick={async (e) => {
                e.preventDefault();

                const { href } = e.currentTarget;
                // This URL parameter will stop the Shopify auto-redirect to Shop Pay checkout flow
                const bypassShopPayUrl = `${href}${
                  href.includes('?') ? '&' : '?'
                }skip_shop_pay=true`;

                // This state is to primarily prevent frustration clicks while the session
                // is refreshing / page is loading which is dramatized on slower connections.
                setIsCheckoutLoading(true);

                const multipass = await refreshSession(client, {
                  return_to: bypassShopPayUrl
                });

                onCheckoutClick(
                  multipass ? multipass.url : bypassShopPayUrl
                ).catch(() => {
                  setIsCheckoutLoading(false);
                });
              }}
              data-track-label="checkout"
            >
              Checkout -{' '}
              {formatPrice(
                Number(subtotalPrice - discountAmount).toFixed(2),
                currencyCode
              ).replace(/\.00$/, '')}
              <Styled.VisuallyHidden data-label="miniCart_currency">
                {currencyCode}
              </Styled.VisuallyHidden>
              <Styled.VisuallyHidden data-label="miniCart_subtotal">
                {Number(subtotalPrice - discountAmount).toFixed(2)}
              </Styled.VisuallyHidden>
            </Button>
            {showMiniCartDiscountButton && (
              <Button
                isFull
                variant="secondary"
                onClick={() => {
                  sessionStorage.setItem('newCustomerOfferStep', 'emailForm');
                  setViewCouponDialogDisplay('modal');
                }}
              >
                {`Get ${siteWideDiscountPercentage}% Off Your First Order`}
              </Button>
            )}
            {showViewBagLink && (
              <Button
                variant="secondary"
                isGhost
                size="large"
                isFull
                data-label={
                  medium ? 'miniCart_viewBag' : 'miniCart_continueShopping'
                }
                data-track-label={viewBagText.toLowerCase()}
                {...secondaryButtonProps}
              >
                {viewBagText}
              </Button>
            )}
          </Styled.ButtonGroup>
        )}
      </Styled.CartFooter>
    </>
  );
};

CartFooter.propTypes = {
  /**
   * Price of the cart before duties, shipping and taxes, and any discounts.
   */
  subtotalPrice: PropTypes.string,
  /**
   * Currency code
   */
  currencyCode: PropTypes.string,
  /**
   * Endpoint to complete a checkout
   */
  checkoutUrl: PropTypes.string,
  /**
   * `true` if the Cart contains at-least one giftable product
   */
  hasGiftableProduct: PropTypes.bool,
  /**
   * A function that is called when the Checkout button is clicked.
   */
  onCheckoutClick: PropTypes.func,
  /**
   * displays a View Bag link when `true`
   */
  showViewBagLink: PropTypes.bool,
  /**
   * Discount code, if applicable
   */
  discountCode: PropTypes.string,
  /**
   * Total discount applied to cart, if applicable
   */
  discountAmount: PropTypes.number,
  /**
   * Total discount percentage applied to cart, if applicable
   */
  discountPercentage: PropTypes.number,
  /**
   * Total number of items in the cart
   */
  totalItems: PropTypes.number,
  /**
   * pass through styled component prop
   */
  className: PropTypes.string,
  /**
   * callback function to display gift options dialog
   */
  onGiftOptionsClick: PropTypes.func.isRequired,
  /**
   * feature flag for prioritized gift messaging
   */
  isGiftingPrioritized: PropTypes.bool,
  /**
   * Site config object from Contentful
   */
  siteConfig: PropTypes.shape({
    is_discount_auto_applied: PropTypes.shape({
      value: PropTypes.bool
    }),
    coupon_code_percentage: PropTypes.shape({
      value: PropTypes.string
    })
  }),
  /**
   * Function to set the view to display for the coupon dialog
   */
  setViewCouponDialogDisplay: PropTypes.func,
  /**
   * Function to close cart dialog
   */
  onRequestClose: PropTypes.func
};

CartFooter.defaultProps = {
  subtotalPrice: '0.0',
  currencyCode: null,
  checkoutUrl: null,
  onCheckoutClick: () => {},
  hasGiftableProduct: false,
  discountCode: null,
  discountAmount: 0,
  discountPercentage: 20,
  totalItems: 0,
  showViewBagLink: false,
  className: null,
  isGiftingPrioritized: false,
  onRequestClose: null
};

export default CartFooter;
